import { useEffect, useMemo, useRef, useState } from 'react';
import useLocales from 'src/hooks/useLocales';

// @mui
import {
  Box,
  Card,
  Table,
  Button,
  TableBody,
  Container,
  TableContainer,
  Grid,
  Stack,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  TableCell,
  Autocomplete,
  Chip,
  IconButton,
  FormControlLabel,
  Switch,
} from '@mui/material';

import { observer } from 'mobx-react-lite';

import HeaderBreadcrumbs from 'src/components/HeaderBreadcrumbs';
import Iconify from 'src/components/Iconify';

import useSettings from 'src/hooks/useSettings';

import { PATH_DASHBOARD } from 'src/routes/paths';
import Page from 'src/components/Page';
import { useStore } from 'src/stores/store';
import {
  IRentPaymentParams,
  IRentPaymentReport,
} from 'src/@types/Choqorak/MainTables/RentPayment.ts/RentPayment';

import { Controller, useForm } from 'react-hook-form';
import LocalizDatePicker from 'src/sections/common/LocalizDatePicker';
import Loader from 'src/components/loader/Loader';

import { GetMonthName } from 'src/sections/common/GetMonthName';
import { DateConverter } from 'src/sections/common/DateConverter';
import Label from 'src/components/Label';
import ElectricityPaymentDetailsToolbar from './ElectricityPaymentDetailsToolbar';
import * as XLSX from 'xlsx';
import { Icon } from '@iconify/react';
import { themeMode } from 'src/utils/general';
import { GetCurrencySymbol } from 'src/sections/common/GetCurrencySymbol';

// ----------------------------------------------------------------------
export default observer(function ElectricityPaymentReportIndex() {
  const { ElectricityPaymentStore, ChoqorakCommonDropdown } = useStore();
  const { translate } = useLocales();
  const [checked, setChecked] = useState(false);
  const { loadElectricityPaymentReport, loadElectricityPaymentDailyReport, ElectricitypaymentReportResult } = ElectricityPaymentStore;
  const { loadBlockeDDL, BlockOption, loadFloorDDL, FloorOption, loadShopDDL, ShopOption, loadCustomerDDL,
    CustomerOption, } =
    ChoqorakCommonDropdown;

  const { themeStretch } = useSettings();
  const [isloading, setIsloading] = useState(false);
  const [fromYear, setFromYear] = useState<string>(''); // State for the year
  const [toYear, setToYear] = useState<string>(''); // State for the year
  const [fromMonth, setFromMonth] = useState<string>(''); // State for the month
  const [toMonth, setToMonth] = useState<string>(''); // State for the month
  const [block, setBlock] = useState<any>();
  const [floor, setFloor] = useState<any>();
  const [shop, setShop] = useState<any>();
  const [customer, setCustomer] = useState<any>();
  let componentRef = useRef<any>(null);

  const defaultValues = useMemo<IRentPaymentReport>(
    () => ({
      fromYear: new Date(),
      toYear: new Date(),
    }),
    []
  );
  const methods = useForm<IRentPaymentReport>({
    defaultValues,
  });
  const { watch, control } = methods;
  const val = watch();
  const fromDate = watch('fromYear'); // Watching the 'years' field
  const toDate = watch('toYear'); // Watching the 'years' field

  useEffect(() => {
    loadBlockeDDL();
    loadFloorDDL();
    loadCustomerDDL();
    if (floor) {
      loadShopDDL(floor, true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block, shop, floor]);

  useEffect(() => {
    if (fromDate) {
      const changeDateToDateFormat = new Date(fromDate);

      // Extract Persian year and month
      const persianYear = changeDateToDateFormat.toLocaleString('fa-IR-u-ca-persian', {
        year: 'numeric',
      });
      const persianMonth = changeDateToDateFormat.toLocaleString('fa-IR-u-ca-persian', {
        month: 'numeric',
      });

      // Convert Persian numerals to Western numerals
      const westernYear = persianYear.replace(/[۰-۹]/g, (digit) =>
        '۰۱۲۳۴۵۶۷۸۹'.indexOf(digit).toString()
      );
      const westernMonth = persianMonth.replace(/[۰-۹]/g, (digit) =>
        '۰۱۲۳۴۵۶۷۸۹'.indexOf(digit).toString()
      );

      // Update state with Western year and month
      setFromYear(westernYear);
      setFromMonth(westernMonth);
    }
    if (toDate) {
      const changeDateToDateFormat = new Date(toDate);

      // Extract Persian year and month
      const persianYear = changeDateToDateFormat.toLocaleString('fa-IR-u-ca-persian', {
        year: 'numeric',
      });
      const persianMonth = changeDateToDateFormat.toLocaleString('fa-IR-u-ca-persian', {
        month: 'numeric',
      });

      // Convert Persian numerals to Western numerals
      const westernYear = persianYear.replace(/[۰-۹]/g, (digit) =>
        '۰۱۲۳۴۵۶۷۸۹'.indexOf(digit).toString()
      );
      const westernMonth = persianMonth.replace(/[۰-۹]/g, (digit) =>
        '۰۱۲۳۴۵۶۷۸۹'.indexOf(digit).toString()
      );

      // Update state with Western year and month
      setToYear(westernYear);
      setToMonth(westernMonth);
    }
  }, [fromDate, toDate]);

  const Search = async (e: any) => {
    e.preventDefault();
    setIsloading(true);
    if (!checked) {
      loadElectricityPaymentReport({
        fromYear: Number(fromYear),
        fromMonth: Number(fromMonth),
        toYear: Number(toYear),
        toMonth: Number(toMonth),
        customerId: Number(customer),
        blockId: block,
        floorId: floor,
        shopId: shop,
      })
        .then((res) => {
          setIsloading(false);
        })
        .catch((err) => {
          setIsloading(false);
        });
    } else {
      loadElectricityPaymentDailyReport({
        paymentDate: new Date(val.paymentDate!),
        blockId: block,
        floorId: floor,
        shopId: shop,
      })
        .then((res) => {
          setIsloading(false);
        })
        .catch((err) => {
          setIsloading(false);
        });
    }
  };

  const exportToExcel = () => {
    const data =
      ElectricitypaymentReportResult?.list?.map((row, index) => ({
        No: index + 1,
        Year: row.year,
        Month: row.month,
        ContractNumber: row.contractNumber || '-',
        CustomerName: row.customerName || '-',
        CurrencyId: row.currencyId,
        ShopCode: row.shopCode || '-',
        RecievedByUserName: row.recievedByUserName || '-',
        TotalPayment: row.totalPayment ?? '-',
        PaidAmount: row.paidAmount ?? '-',
        RemainAmount: row.remainAmount ?? '-',
        PaymentDate: row.paymentDate ? new Date(row.paymentDate).toLocaleDateString('fa-IR') : '-',
        Description: row.description || '-',
      })) || [];

    if (data.length === 0) {
      alert('No data available to export.');
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Electricity Payment Report');

    XLSX.writeFile(workbook, 'Electricity_Payment_Report.xlsx');
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
  };

  return (
    <Page title={translate('ElectricityPayment.Title')}>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading={translate('ElectricityPayment.ElectricityPaymentReport')}
          links={[
            { name: `${translate('ElectricityPayment.Dashboard')}`, href: PATH_DASHBOARD.root },
            { name: `${translate('ElectricityPayment.ElectricityPaymentReport')}` },
          ]}
          action={<></>}
        />

        <Box sx={{ display: 'flex', alignItems: 'left', justifyContent: 'left' }}>
          <IconButton
            sx={{ color: themeMode() === 'light' ? 'black' : 'white' }}
            onClick={exportToExcel}
          >
            <Icon icon="vscode-icons:file-type-excel" width={24} height={24} />
          </IconButton>

          <ElectricityPaymentDetailsToolbar
            Report={ElectricitypaymentReportResult}
            fromYear={Number(fromYear)}
            fromMonth={Number(fromMonth)}
            toYear={Number(toYear)}
            toMonth={Number(toMonth)}
            paymentDate={val.paymentDate!}
            checked={checked}
          />
        </Box>

        <Grid container display="flex" alignItems="center" justifyContent="center">
          <Grid item xs={12} md={12} spacing={2}>
            <Card
              sx={{
                p: 3,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Grid item xs={12} md={12}>
                <Box
                  sx={{
                    display: 'grid',
                    columnGap: 2,
                    rowGap: 3,
                    gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(6, 1fr)' },
                  }}
                >
                  {!checked ? (
                    <>
                      <LocalizDatePicker
                        name="fromYear"
                        label={translate('ElectricityPayment.FromDate')}
                        control={control}
                        size={'small'}
                        views={['year', 'month']}
                      />

                      <LocalizDatePicker
                        name="toYear"
                        label={translate('ElectricityPayment.ToDate')}
                        control={control}
                        size={'small'}
                        views={['year', 'month']}
                      />
                    </>
                  ) : (
                    <LocalizDatePicker
                      name="paymentDate"
                      label={translate('RentPayment.PaymentDate')}
                      control={control}
                      size={'small'}
                    />
                  )}

                  <Controller
                    name="blockId"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <Autocomplete
                        fullWidth
                        freeSolo
                        // onChange={(event, newValue) => setBlock(newValue.value)}
                        onChange={(event, newValue) => {
                          if (newValue) {
                            field.onChange(newValue);
                            setBlock(newValue.value);
                          } else {
                            field.onChange(null);
                            setBlock(undefined);
                          }
                        }}
                        options={BlockOption.map((option: any) => option)}
                        value={field.value}
                        getOptionLabel={(option) => option.text}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={translate('ElectricityPayment.Block')}
                            error={!!error}
                            size="small"
                            helperText={error?.message}
                          />
                        )}
                      />
                    )}
                  />

                  <Controller
                    name="customerId"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <Autocomplete
                        fullWidth
                        freeSolo
                        onChange={(event, newValue) => {
                          if (newValue) {
                            field.onChange(newValue);
                            setCustomer(newValue.value);
                          } else {
                            field.onChange(null);
                            setCustomer(undefined);
                          }
                        }}
                        options={CustomerOption.map((option: any) => option)}
                        value={field.value}
                        getOptionLabel={(option) => option.text}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              {...getTagProps({ index })}
                              key={option.value}
                              size="small"
                              label={option.value}
                            />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={translate('Customer.Customer')}
                            error={!!error}
                            size="small"
                            helperText={error?.message}
                          />
                        )}
                      />
                    )}
                  />




                  <Controller
                    name="floorId"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <Autocomplete
                        fullWidth
                        freeSolo
                        // onChange={(event, newValue) => setFloor(newValue.value)}
                        onChange={(event, newValue) => {
                          if (newValue) {
                            field.onChange(newValue);
                            setFloor(newValue.value);
                          } else {
                            field.onChange(null);
                            setFloor(undefined);
                          }
                        }}
                        options={FloorOption.map((option: any) => option)}
                        value={field.value}
                        getOptionLabel={(option) => option.text}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={translate('ElectricityPayment.Floor')}
                            error={!!error}
                            size="small"
                            helperText={error?.message}
                          />
                        )}
                      />
                    )}
                  />

                  <Controller
                    name="shopId"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <Autocomplete
                        fullWidth
                        freeSolo
                        // onChange={(event, newValue) => setShop(newValue.value)}
                        onChange={(event, newValue) => {
                          if (newValue) {
                            field.onChange(newValue);
                            setShop(newValue.value);
                          } else {
                            field.onChange(null);
                            setShop(undefined);
                          }
                        }}
                        options={ShopOption.map((option: any) => option)}
                        value={field.value}
                        getOptionLabel={(option) => option.text}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={translate('ElectricityPayment.Shop')}
                            error={!!error}
                            size="small"
                            helperText={error?.message}
                          />
                        )}
                      />
                    )}
                  />

                  <Button
                    sx={{ width: '120px' }}
                    variant="contained"
                    onClick={Search}
                    startIcon={<Iconify icon="eva:search-fill" />}
                  >
                    {translate('ElectricityPayment.Filter')}
                  </Button>

                  <Controller
                    name="dailyReport"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            {...field}
                            onChange={(e) => {
                              field.onChange(e); // Notify React Hook Form of the value change
                              handleChange(e); // Handle the switch state change
                            }}
                            checked={field.value}
                          />
                        }
                        label={translate('RentPayment.DailyReport')}
                        labelPlacement="end"
                      />
                    )}
                  />
                </Box>
              </Grid>
            </Card>
          </Grid>
        </Grid>
        {isloading ? (
          <Loader />
        ) : (
          <>
            <Grid item xs={12} md={12} sx={{ mt: 2 }}>
              <Stack sx={{ p: 1 }}>
                <TableContainer sx={{ maxHeight: 500 }} component={Paper}>
                  <Table stickyHeader sx={{ minWidth: 700 }} aria-label="customized table">
                    <TableHead>
                      <TableRow>
                        <TableCell>{translate('ElectricityPayment.No')}</TableCell>
                        <TableCell>{translate('ElectricityPayment.Year')}</TableCell>
                        <TableCell>{translate('ElectricityPayment.Month')}</TableCell>
                        <TableCell>{translate('ElectricityPayment.CustomerName')}</TableCell>
                        <TableCell>{translate('ElectricityPayment.ShopCode')}</TableCell>
                        <TableCell>{translate('ElectricityPayment.RecievedByUserName')}</TableCell>
                        <TableCell>{translate('ElectricMeter.LastElectricMeterDegree')}</TableCell>
                        <TableCell>
                          {translate('ElectricityPayment.CurrentElectricMeterDegree')}
                        </TableCell>
                        <TableCell>{translate('ElectricityPayment.CurrentAmount')}</TableCell>
                        <TableCell>{translate('RentPayment.RemainFromThePast')}</TableCell>
                        <TableCell>{translate('ElectricityPayment.PaymentAmount')}</TableCell>
                        <TableCell>{translate('ElectricityPayment.PaidAmount')}</TableCell>
                        <TableCell>{translate('ElectricityPayment.RemainAmount')}</TableCell>
                        <TableCell>{translate('ElectricityPayment.PaymentDate')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ElectricitypaymentReportResult?.list.map((row, index) => (
                        <TableRow key={index}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>
                            <Label color="warning">{row.year}</Label>
                          </TableCell>
                          <TableCell>
                            <Label color="success">{GetMonthName(row.month)}</Label>
                          </TableCell>
                          <TableCell>{row.customerName}</TableCell>
                          <TableCell>{row.shopCode}</TableCell>
                          <TableCell>{row.recievedByUserName}</TableCell>
                          <TableCell align="center">{row.previousElectricMeterDegree}</TableCell>
                          <TableCell align="center">{row.currentElectricMeterDegree}</TableCell>
                          <TableCell align="center">
                            {row.currentDateRangePayment} {GetCurrencySymbol('afn')}
                          </TableCell>
                          <TableCell align="center">
                            {row.remnantsOfThePast} {GetCurrencySymbol('afn')}
                          </TableCell>
                          <TableCell>{row.totalPayment}{' '}{GetCurrencySymbol('afn')}</TableCell>
                          <TableCell>{row.paidAmount}{' '}{GetCurrencySymbol('afn')}</TableCell>
                          <TableCell>
                            {row.remainAmount === 0 ? (
                              <Label color="warning">{translate('ElectricityPayment.Paid')}</Label>
                            ) : (
                              ` ${row.remainAmount} ${GetCurrencySymbol('afn')} `
                            )}
                          </TableCell>
                          <TableCell>
                            {row.paymentDate !== null ? (
                              <DateConverter date={row.paymentDate} />
                            ) : (
                              ''
                            )}
                          </TableCell>
                        </TableRow>
                      ))}

                    </TableBody>
                  </Table>
                </TableContainer>
              </Stack>
            </Grid>
            <Grid item xs={12} sx={{ width: '100%', mb: 2, mt: 2 }}>
              <div>
                <h3 className="text-center text-xl font-bold border-4 border-black p-2">
                  {translate('Shop.Report.ReportSummary')}
                </h3>
                <TableContainer component={Paper} sx={{ paddingLeft: 2, paddingRight: 2 }}>
                  <Table sx={{ minWidth: '100%' }} size="small" aria-label="summary table">
                    <TableBody>
                      {/* First Row */}
                      <TableRow>
                        <TableCell align="left" sx={{ fontSize: '16px' }}>
                          {translate('ReportSummary.TotalPaymentRecords')}
                        </TableCell>
                        <TableCell align="left" sx={{ fontSize: '16px' }}>
                          {translate('ReportSummary.TotalPaidAmount')}
                        </TableCell>


                        <TableCell align="left" sx={{ fontSize: '16px' }}>
                          {translate('ReportSummary.TotalRemainAmount')}
                        </TableCell>

                      </TableRow>
                      <TableRow>
                        <TableCell align="left" sx={{ fontWeight: 'bold', fontSize: '16px' }}>
                          {/* {ElectricitypaymentReportResult?.report.} */}
                        </TableCell>
                        <TableCell align="left" sx={{ fontWeight: 'bold', fontSize: '16px' }}>
                          {ElectricitypaymentReportResult?.report
                            ?.flat()
                            ?.reduce((sum, item) => sum + item.totalPaidAmount, 0)}
                        </TableCell>


                        <TableCell align="left" sx={{ fontWeight: 'bold', fontSize: '16px' }}>
                          {ElectricitypaymentReportResult?.report
                            ?.flat()
                            ?.reduce((sum, item) => sum + item.totalRemainAmount, 0)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
            </Grid>
          </>
        )}
      </Container>
    </Page>
  );
});
